/**
 * VideoRecorder - Record cutscene playback to video using MediaRecorder API
 */

export interface VideoRecorderOptions {
  /** Video MIME type (default: video/webm) */
  mimeType?: string;
  /** Video bitrate in bps (default: 5000000 = 5Mbps) */
  videoBitsPerSecond?: number;
  /** Frame rate (default: 60) */
  frameRate?: number;
}

export interface VideoRecorderState {
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  blob: Blob | null;
}

export class VideoRecorder {
  private canvas: HTMLCanvasElement | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private startTime: number = 0;
  private options: Required<VideoRecorderOptions>;
  private onStateChange?: (state: VideoRecorderState) => void;

  constructor(options: VideoRecorderOptions = {}) {
    this.options = {
      mimeType: options.mimeType || this.getSupportedMimeType(),
      videoBitsPerSecond: options.videoBitsPerSecond || 5000000,
      frameRate: options.frameRate || 60,
    };
  }

  /**
   * Get the best supported MIME type for video recording
   */
  private getSupportedMimeType(): string {
    const types = [
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
      "video/mp4",
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return "video/webm";
  }

  /**
   * Set the canvas element to record
   */
  setCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
  }

  /**
   * Set callback for state changes
   */
  setOnStateChange(callback: (state: VideoRecorderState) => void): void {
    this.onStateChange = callback;
  }

  /**
   * Start recording
   */
  start(): boolean {
    if (!this.canvas) {
      console.error("[VideoRecorder] No canvas set");
      return false;
    }

    if (this.mediaRecorder?.state === "recording") {
      console.warn("[VideoRecorder] Already recording");
      return false;
    }

    try {
      // Get canvas stream
      const stream = this.canvas.captureStream(this.options.frameRate);

      // Create MediaRecorder
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: this.options.mimeType,
        videoBitsPerSecond: this.options.videoBitsPerSecond,
      });

      this.recordedChunks = [];
      this.startTime = Date.now();

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.notifyStateChange();
      };

      this.mediaRecorder.onerror = (event) => {
        console.error("[VideoRecorder] Error:", event);
      };

      // Request data every 100ms for smoother recording
      this.mediaRecorder.start(100);

      this.notifyStateChange();
      return true;
    } catch (error) {
      console.error("[VideoRecorder] Failed to start:", error);
      return false;
    }
  }

  /**
   * Stop recording and return the recorded blob
   */
  stop(): Promise<Blob | null> {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === "inactive") {
        resolve(null);
        return;
      }

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.recordedChunks, {
          type: this.options.mimeType,
        });
        this.notifyStateChange();
        resolve(blob);
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Pause recording
   */
  pause(): void {
    if (this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.pause();
      this.notifyStateChange();
    }
  }

  /**
   * Resume recording
   */
  resume(): void {
    if (this.mediaRecorder?.state === "paused") {
      this.mediaRecorder.resume();
      this.notifyStateChange();
    }
  }

  /**
   * Get current state
   */
  getState(): VideoRecorderState {
    const isRecording = this.mediaRecorder?.state === "recording";
    const isPaused = this.mediaRecorder?.state === "paused";
    const duration = isRecording || isPaused ? Date.now() - this.startTime : 0;
    const blob =
      this.recordedChunks.length > 0
        ? new Blob(this.recordedChunks, { type: this.options.mimeType })
        : null;

    return {
      isRecording,
      isPaused,
      duration,
      blob,
    };
  }

  /**
   * Download the recorded video
   */
  download(filename: string = "cutscene"): void {
    const blob = this.getBlob();
    if (!blob) {
      console.error("[VideoRecorder] No recording to download");
      return;
    }

    const extension = this.options.mimeType.includes("mp4") ? "mp4" : "webm";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Get the recorded blob
   */
  getBlob(): Blob | null {
    if (this.recordedChunks.length === 0) return null;
    return new Blob(this.recordedChunks, { type: this.options.mimeType });
  }

  /**
   * Clear recorded data
   */
  clear(): void {
    this.recordedChunks = [];
    this.startTime = 0;
    this.notifyStateChange();
  }

  private notifyStateChange(): void {
    this.onStateChange?.(this.getState());
  }
}

/**
 * Hook-style helper for video recording in React components
 */
export function createVideoRecorder(
  options?: VideoRecorderOptions,
): VideoRecorder {
  return new VideoRecorder(options);
}





